import { cn } from "@/lib/utils";

export type FilterTabOption = {
  label: string;
  value: string | null;
  width?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  badgeActiveClassName?: string;
  badgeInactiveClassName?: string;
};

type FilterTabButtonProps = {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  width?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  badgeActiveClassName?: string;
  badgeInactiveClassName?: string;
};

export const FilterTabButton = ({
  label,
  count,
  active,
  onClick,
  width,
  activeClassName = "bg-[#3562f8] text-white",
  inactiveClassName = "text-gray-900 hover:bg-gray-100",
  badgeActiveClassName = "bg-white/20 text-white",
  badgeInactiveClassName = "bg-rose-600 text-white",
}: FilterTabButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "text-md flex !w-full items-center justify-between gap-2 rounded-md px-4 py-2 font-medium whitespace-nowrap transition-colors sm:w-auto",
      width,
      active ? activeClassName : inactiveClassName,
    )}
  >
    <span>{label}</span>
    {count !== undefined && (
      <span
        className={cn(
          "text-md flex items-center justify-center rounded-full px-1.5 font-semibold",
          active ? badgeActiveClassName : badgeInactiveClassName,
        )}
      >
        {count}
      </span>
    )}
  </button>
);

type FilterTabsProps = {
  options: FilterTabOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  counts?: Map<string | null, number | undefined>;
  className?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  badgeActiveClassName?: string;
  badgeInactiveClassName?: string;
};

export const FilterTabs = ({
  options,
  value,
  onChange,
  counts,
  className,
  activeClassName,
  inactiveClassName,
  badgeActiveClassName,
  badgeInactiveClassName,
}: FilterTabsProps) => (
  <div
    className={cn(
      "inline-flex w-fit items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-[0_1px_1px_rgba(0,0,0,0.1)]",
      className,
    )}
  >
    {options.map(({ label, value: optValue, width, ...optColors }) => (
      <FilterTabButton
        key={String(optValue)}
        label={label}
        count={counts?.get(optValue)}
        active={value === optValue}
        onClick={() => onChange(optValue)}
        width={width}
        activeClassName={optColors.activeClassName ?? activeClassName}
        inactiveClassName={optColors.inactiveClassName ?? inactiveClassName}
        badgeActiveClassName={optColors.badgeActiveClassName ?? badgeActiveClassName}
        badgeInactiveClassName={optColors.badgeInactiveClassName ?? badgeInactiveClassName}
      />
    ))}
  </div>
);
